/*
  Каркас кабинета: кто вошёл, что ему доступно и какой раздел открыт.

  Экран-контейнер и не содержит логики сверх этого (Глава 8.7): решения о том,
  что показывать, принимает сервер — он же считает разрешения роли и режим
  лицензии. Клиент их не переоткрывает, иначе два ответа на один вопрос
  разойдутся.
*/

import { useCallback, useEffect, useRef, useState } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'

import { api } from './api/client.js'
import { AccessProvider } from './components/AccessContext.jsx'
import Sidebar from './components/Sidebar.jsx'
import { Loading, Notice } from './components/Common.jsx'
import { onSessionLost } from './api/session.js'
import LoginPage from './pages/LoginPage.jsx'
import CabinetRoutes from './pages/CabinetRoutes.jsx'
import DebtorPage, { DEBTOR_PATH } from './pages/DebtorPage.jsx'
import PlatformAdminPage from './pages/PlatformAdminPage.jsx'

/*
  Экраны, привозящие число «горит» в СВОЁМ ответе: дашборд отдаёт
  `burning.accounts`, реестр - `burning`. Открыт один из них - оболочка за
  значком не обращается: число уже едет, а отдельный запрос означал бы второй
  разбор всего портфеля на той же загрузке (строка 6 реестра компромиссов).

  Список обязан совпадать с маршрутами, которым ниже передан `onBurning`.
  Разойдётся - значок на этом экране не появится, поэтому оба конца прибиты
  проверками: `dashboard.test.jsx` и `registry_badge.test.jsx` требуют, чтобы
  экран число сообщал, `menu_badge.test.jsx` - чтобы вне этих экранов его
  добывала оболочка.
*/
const SCREENS_WITH_BURNING = ['/dashboard', '/registry']

export default function App() {
  const [profile, setProfile] = useState(null)
  const [checked, setChecked] = useState(false)
  const [failure, setFailure] = useState(null)
  const [burning, setBurning] = useState(0)
  const navigate = useNavigate()
  const location = useLocation()

  // Страница должника живёт вне кабинета: у неё нет ни входа, ни роли, ни меню.
  // Проверять сессию перед её показом нельзя — должник её не имеет, и ожидание
  // ответа «вы не вошли» задержало бы единственный экран, ради которого он
  // пришёл.
  //
  // Сравнение идёт по ГРАНИЦЕ сегмента, а не по началу строки. 07.09.2026:
  // было `startsWith(DEBTOR_PATH)`, и под «/d» попал «/dashboard» — главный
  // экран кабинета отдавал должнику «Ссылка неполная» вместо денег. Короткий
  // адрес выбран ради длины ссылки в сообщении, и любой будущий раздел на ту
  // же букву наступил бы на это снова.
  const isDebtorPage =
    location.pathname === DEBTOR_PATH || location.pathname.startsWith(`${DEBTOR_PATH}/`)

  /*
    🔴 «Вы не вошли» - это ТОЛЬКО ответ 401, и ничто другое.

    До 09.09.2026 здесь стоял `.catch(() => setProfile(null))`: любая неудача
    проверки означала форму входа. Обрыв сети, перезапуск сервера, пятисотый
    ответ - человек видел «войдите заново» при живой сессии, вводил пароль и
    считал, что его выбросило по сроку. Форма входа на месте отказа сервера -
    неправда о том, что произошло, и она же уводит от настоящей причины.
  */
  const check = useCallback(() => {
    // Пока идёт проверка, экран обязан показывать «проверяем», а не решение.
    // Без сброса `checked` повторная проверка на секунду обнажала форму входа:
    // отказ уже убран, ответа ещё нет, и каркас показывал «не вошёл». Видно
    // это только на живом ожидании - найдено осмотром в браузере 09.09.2026.
    setChecked(false)
    setFailure(null)
    return api
      .me()
      .then(setProfile)
      .catch((error) => {
        if (error.status === 401) setProfile(null)
        else setFailure(error)
      })
      .finally(() => setChecked(true))
  }, [])

  useEffect(() => {
    if (isDebtorPage) return
    check()
  }, [isDebtorPage, check])

  // Сессия кончилась посреди работы (ответ 401 на любой запрос): назад на вход, а не экраны
  // с надписью «войдите заново» без кнопки входа (аудит 28.10.2026, раздел 5.3).
  useEffect(() => onSessionLost(() => setProfile(null)), [])

  /*
    Число «горит» у пункта «Реестр» добывает сам каркас, а не экраны.

    До 11.09.2026 его ставили побочным действием дашборд и реестр - те
    единственные два экрана, которым оно и так приезжало в ответе. В остальных
    восьми разделах значка не было вовсе, а перезагрузка страницы в любом из
    них гасила его до тех пор, пока человек не откроет реестр (замечание
    владельца: «цифры появляются только в момент, когда нажимаешь на реестр»).
    Причина в том, что значок - свойство ПОРТФЕЛЯ и часть меню, а меню живёт
    здесь; спрашивать его у открытой страницы значило спрашивать не у того.

    Обращение делается РАЗ НА ЗАГРУЗКУ страницы и только если открытый раздел
    число не привозит сам. Ровно в этом цена: запрос - второй полный разбор
    портфеля, и на дашборде он был бы вторым подряд. Замер 11.09 показал, чем
    это грозит: сквозной прогон упёрся в защиту «200 обращений с адреса в
    минуту» - пик 205 при пороге 200.

    Открытый раздел держится в `useRef`, а не в зависимостях эффекта: попади
    он в зависимости - эффект срабатывал бы на каждом переходе между разделами,
    то есть разбор портфеля шёл бы на каждый клик по меню, хотя число к тому
    времени уже известно. Право проверяется до запроса: просить значок, которого
    роли не покажут, значит получать отказ на каждой загрузке. Своя неудача
    молчит - значок подсказывает, а не отвечает за экран.
  */
  const openedAt = useRef(location.pathname)
  useEffect(() => {
    openedAt.current = location.pathname
  })

  useEffect(() => {
    if (!profile?.permissions?.includes('view_registry')) {
      // Сюда же приходим после выхода: иначе число прежнего заказчика
      // осталось бы на экране входа следующего.
      setBurning(0)
      return
    }
    if (SCREENS_WITH_BURNING.includes(openedAt.current)) return
    api
      .badges()
      .then((counts) => setBurning(counts.burning))
      .catch(() => null)
  }, [profile])

  const onLogout = useCallback(async () => {
    await api.logout().catch(() => null)
    setProfile(null)
    navigate('/login')
  }, [navigate])

  if (isDebtorPage) return <DebtorPage />

  if (!checked) return <Loading what="Проверяем, кто вошёл" />

  if (failure) {
    // Сессия могла остаться живой: не отвечает сервер, а не она. Поэтому
    // экран предлагает повторить проверку, а не войти заново.
    return (
      <div className="login">
        <div className="login__card">
          <div className="login__brand">Арбитр ЖКХ</div>
          <Notice error={failure} wide />
          <button className="button login__submit" type="button" onClick={check}>
            Проверить ещё раз
          </button>
        </div>
      </div>
    )
  }

  if (!profile) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage onEntered={setProfile} />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  // Роль без единого разрешения — это владелец платформы: его место в отдельной
  // панели. Любой раздел кабинета ответит ему отказом, и красная плашка на
  // первом же экране читается как поломка входа, а не как устройство ролей
  // (замечено владельцем 10.09.2026 при первом входе в бою). Признак берётся по
  // разрешениям, а не по названию роли: список ролей растёт, и следующая такая
  // же снова упёрлась бы в отказ.
  if (!profile.permissions?.length) {
    return <PlatformAdminPage profile={profile} onLogout={onLogout} />
  }

  const start = profile.landing === 'dashboard' ? '/dashboard' : '/registry'

  return (
    <div className="shell">
      <Sidebar profile={profile} burning={burning} onLogout={onLogout} />
      <main className="work">
        <AccessProvider value={profile}>
          {/* Режим «только чтение» (§3.1.6): одна плашка на всех экранах объясняет,
              почему действий нет. Без неё кнопки просто пропадали бы или серели. */}
          {profile.is_readonly ? (
            <Notice kind="warn" wide>
              <div className="notice__what">
                Лицензия не оплачена, поэтому действия временно недоступны.
              </div>
              <div className="notice__do">
                Данные видны и выгружаются, но формировать документы, подавать и рассылать нельзя.
                Оплатите лицензию - доступ к действиям восстановится сразу после поступления
                средств.
              </div>
            </Notice>
          ) : null}
          <CabinetRoutes profile={profile} start={start} onBurning={setBurning} />
        </AccessProvider>
      </main>
    </div>
  )
}
